<template>
  <QMarkupTable class="permissions-table">
    <thead class="permissions-table-header">
      <tr>
        <th class="text-left" />
        <th
          v-for="role in sortedRoles"
          :key="role.id"
          class="text-center"
        >
          {{ capitalize(role.name) }}
        </th>
      </tr>
    </thead>

    <tbody
      v-for="group in sortedPermissionsGroups"
      :key="group.name"
      class="q-pt-md"
    >
      <tr>
        <td
          class="text-left"
          colspan="8"
        >
          <h3>
            {{ group.name }}
          </h3>
        </td>
      </tr>
      <tr />
      <tr
        v-for="permission in group.permissions"
        :key="permission.id"
      >
        <td class="text-left">
          {{ formattedPermission(permission) }}
        </td>

        <td
          v-for="tdRole in sortedRoles"
          :key="tdRole.id"
          class="text-center"
        >
          <QIcon
            v-bind="iconAttributes(tdRole, permission)"
            class="bold"
            size="1.5em"
          />
        </td>
      </tr>
    </tbody>
  </QMarkupTable>
</template>

<script>
import { storeToRefs } from "pinia";

import { capitalize } from "shared/helpers/string";

import { useGlobalAdminStore } from "src/stores/global";

const ROLE_ORDER = {
  super: 0,
  standard: 1,
  reporting: 2,
  government: 3,
  agency: 4,
  "government 2": 5,
  restricted: 6,
};

const PERMISSIONS_GROUPS = [
  {
    name: "Admin",
    permissions: [28, 6, 14, 20, 30, 18, 17, 33, 43],
  },
  {
    name: "Reporting",
    permissions: [3, 16, 4, 5, 12, 13, 7, 32],
  },
  {
    name: "Media/Transcripts",
    permissions: [35, 36, 1, 10, 27],
  },
  {
    name: "General",
    permissions: [8, 15, 2, 34, 29],
  },
];

const OUTREACH_PERMISSIONS_GROUPS = [
  {
    name: "Admin",
    permissions: [28, 6, 14, 20, 30, 18],
  },
  {
    name: "Outreach",
    permissions: [37, 38, 39, 40, 41, 42],
  },
];

export default {
  name: "RolesPermissionsTable",
  props: {
    roles: {
      type: Array,
      default: () => [],
    },
    product: {
      type: String,
      required: true,
    },
  },
  setup() {
    const globalAdminStore = useGlobalAdminStore();
    const { permissions } = storeToRefs(globalAdminStore);

    return { permissions };
  },
  computed: {
    permissionGroups() {
      if (this.product === "outreach") {
        return OUTREACH_PERMISSIONS_GROUPS;
      }

      return PERMISSIONS_GROUPS;
    },
    roleOrder() {
      return ROLE_ORDER;
    },
    sortedPermissionsGroups() {
      const sortedGroupsWithNames = this.permissionGroups.map(
        ({ name, permissions }) => {
          const permissionNames = permissions.map((permission) =>
            this.permissions.find(
              (matchingPermission) => matchingPermission.id === permission
            )
          );

          return {
            name,
            permissions: permissionNames,
          };
        }
      );

      return sortedGroupsWithNames;
    },
    sortedRoles() {
      return [...this.roles].sort(
        (roleA, roleB) =>
          this.roleOrder[roleA.name] - this.roleOrder[roleB.name]
      );
    },
  },
  methods: {
    capitalize,
    hasPermission(role, permission) {
      return role.permissionsRoles.some(
        (permissionsRole) => permissionsRole.permissionId === permission.id
      );
    },
    iconAttributes(role, permission) {
      return this.hasPermission(role, permission)
        ? { name: "done", class: "text-ds-green-5" }
        : { name: "close", class: "text-ds-red-5" };
    },
    formattedPermission(permission) {
      return capitalize(permission.permission.split("_").splice(1).join(" "));
    },
  },
};
</script>

<style lang="scss" scoped>
.permissions-table {
  height: 500px;
}

.permissions-table-header {
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 10000;
  font-weight: bold;
}

.q-table th {
  font-weight: bold;
}
</style>
